import {announce} from '@github-ui/aria-live'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Label, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {
  type ApplySuggestionRef,
  commitApplyPendingIssueSuggestionsMutation,
} from '../../mutations/apply-pending-issue-suggestions-mutation'
import {
  commitRejectPendingIssueSuggestionsMutation,
  type RejectSuggestionRef,
} from '../../mutations/reject-pending-issue-suggestions-mutation'
import {buildSuggestionRef} from '../../utils/suggestion-ref'
import type {useRecentAgentActivity_issue$key} from './__generated__/useRecentAgentActivity_issue.graphql'
import styles from './RecentAgentActivity.module.css'
import {
  AppliedSuggestionChip,
  buildPendingChipItems,
  PendingLabelGroupChip,
  PendingSuggestionChip,
} from './SuggestionChip'
import {useDismissRecentAgentActivity} from './use-dismiss-recent-agent-activity'
import type {PendingSuggestion} from './use-recent-agent-activity'
import {useRecentAgentActivity} from './use-recent-agent-activity'
import {useRecentAgentActivitySubscription} from './use-recent-agent-activity-subscription'

type RecentAgentActivityProps = {
  issue: useRecentAgentActivity_issue$key
  issueBodyRef: React.RefObject<HTMLDivElement | null>
}

const FEEDBACK_URL = 'https://github.com/orgs/community/discussions/198815'

function countByConfidence(suggestions: PendingSuggestion[]): {
  high_confidence_count: number
  medium_confidence_count: number
  low_confidence_count: number
  no_confidence_count: number
} {
  const counts = {high_confidence_count: 0, medium_confidence_count: 0, low_confidence_count: 0, no_confidence_count: 0}
  for (const suggestion of suggestions) {
    switch (suggestion.confidence) {
      case 'HIGH':
        counts.high_confidence_count++
        break
      case 'MEDIUM':
        counts.medium_confidence_count++
        break
      case 'LOW':
        counts.low_confidence_count++
        break
      default:
        counts.no_confidence_count++
    }
  }
  return counts
}

export function RecentAgentActivity({issue, issueBodyRef}: RecentAgentActivityProps) {
  const {viewerCanUpdateMetadata, issueId, pendingSuggestions} = useRecentAgentActivity(issue)
  const {isDismissed, dismissForIssue, dismissForever} = useDismissRecentAgentActivity(issueId)
  const {sendAnalyticsEvent} = useAnalytics()
  const environment = useRelayEnvironment()

  useRecentAgentActivitySubscription(issueId)

  const pendingSuggestionRows = useMemo(
    () => pendingSuggestions.groups.flatMap(group => group.suggestions.filter(s => s.state === 'PENDING')),
    [pendingSuggestions.groups],
  )
  const pendingCount = pendingSuggestionRows.length

  // Suggestions the viewer just approved in this session. Rendered as transient
  // applied chips so the viewer immediately sees the result of their action.
  // Cleared on unmount/refresh; the server's `pendingSuggestions` field only
  // returns PENDING rows (filtered in the hook), so applied chips never persist.
  const [recentlyApproved, setRecentlyApproved] = useState<PendingSuggestion[]>([])
  const appliedCount = recentlyApproved.length

  const pendingChipItems = useMemo(() => buildPendingChipItems(pendingSuggestionRows), [pendingSuggestionRows])

  const panelRef = useRef<HTMLDivElement | null>(null)

  // After a chip is approved or rejected (individually or in bulk), move focus
  // to the next focusable chip in the list. If none remain, fall back to the
  // kebab "More actions" button, then to the issue body if the whole panel
  // has unmounted, so focus doesn't fall to document.body.
  const focusAfterAction = useCallback(() => {
    // Defer two frames so React can unmount the dismissed chip and any
    // optimistic store update can land.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const panel = panelRef.current
        if (!panel) {
          issueBodyRef.current?.focus()
          return
        }
        const nextChip = panel.querySelector<HTMLButtonElement>('[role="list"] button')
        if (nextChip) {
          nextChip.focus()
          return
        }
        const fallback = panel.querySelector<HTMLButtonElement>('[aria-label="More actions"]')
        fallback?.focus()
      })
    })
  }, [issueBodyRef])

  const handleDismissForIssue = useCallback(() => {
    dismissForIssue()
    focusAfterAction()
  }, [dismissForIssue, focusAfterAction])

  const handleDismissForever = useCallback(() => {
    dismissForever()
    focusAfterAction()
  }, [dismissForever, focusAfterAction])

  const handleApprove = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
        intent_id: suggestion.intentId ?? 'NONE',
      })
      const ref = buildSuggestionRef(suggestion)
      if (!ref) return
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
      })

      // Applying a coding-agent assignment auto-assigns the approver (the
      // current viewer) server-side, resolving their own pending "assign me"
      // suggestion. Surface that as an applied chip too so the result is visible.
      const approved: PendingSuggestion[] = [suggestion]
      if (suggestion.type === 'assignee_assigned' && suggestion.assigneeIsAgent) {
        const viewerLogin = getCurrentUserLogin()
        const approverSuggestion = pendingSuggestionRows.find(
          s => s !== suggestion && s.type === 'assignee_assigned' && s.assigneeLogin === viewerLogin,
        )
        if (approverSuggestion) approved.push(approverSuggestion)
      }
      setRecentlyApproved(prev => [...prev, ...approved])
      announce('Suggestion approved')
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction, pendingSuggestionRows],
  )

  const handleReject = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
        intent_id: suggestion.intentId ?? 'NONE',
      })
      const ref = buildSuggestionRef(suggestion)
      if (!ref) return
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
      })
      announce('Suggestion rejected')
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  // Approve a group of pending suggestions that share an actor (e.g. multiple
  // labels suggested at the same confidence). We fire one mutation with all
  // the refs and an analytics event per suggestion so per-suggestion metrics
  // remain consistent with individual approvals.
  const handleApproveGroup = useCallback(
    (suggestionsToApprove: PendingSuggestion[]) => {
      if (suggestionsToApprove.length === 0) return
      const refs: ApplySuggestionRef[] = []
      const approved: PendingSuggestion[] = []
      for (const suggestion of suggestionsToApprove) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
          confidence: suggestion.confidence ?? 'NONE',
          intent_id: suggestion.intentId ?? 'NONE',
        })
        const ref = buildSuggestionRef(suggestion)
        if (ref) {
          refs.push(ref)
          approved.push(suggestion)
        }
      }
      if (refs.length === 0) return
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestionsToApprove[0]!.actorId,
        suggestions: refs,
      })
      setRecentlyApproved(prev => [...prev, ...approved])
      announce(refs.length === 1 ? 'Suggestion approved' : `${refs.length} suggestions approved`)
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  const handleRejectGroup = useCallback(
    (suggestionsToReject: PendingSuggestion[]) => {
      if (suggestionsToReject.length === 0) return
      const refs: RejectSuggestionRef[] = []
      for (const suggestion of suggestionsToReject) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
          confidence: suggestion.confidence ?? 'NONE',
          intent_id: suggestion.intentId ?? 'NONE',
        })
        const ref = buildSuggestionRef(suggestion)
        if (ref) refs.push(ref)
      }
      if (refs.length === 0) return
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestionsToReject[0]!.actorId,
        suggestions: refs,
      })
      announce(refs.length === 1 ? 'Suggestion rejected' : `${refs.length} suggestions rejected`)
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  const handleApproveAll = useCallback(() => {
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_approved', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      intent_ids: pendingSuggestionRows.map(s => s.intentId ?? 'NONE'),
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: ApplySuggestionRef[]; approved: PendingSuggestion[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildSuggestionRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: [], approved: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
      bucket.approved.push(suggestion)
    }
    const approvedAll: PendingSuggestion[] = []
    for (const [actorId, {refs, approved}] of byActor) {
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
      })
      approvedAll.push(...approved)
    }
    if (approvedAll.length > 0) setRecentlyApproved(prev => [...prev, ...approvedAll])
    const count = pendingSuggestionRows.length
    announce(count === 1 ? 'Suggestion approved' : `${count} suggestions approved`)
    focusAfterAction()
  }, [pendingSuggestionRows, environment, issueId, sendAnalyticsEvent, focusAfterAction])

  const handleRejectAll = useCallback(() => {
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_rejected', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      intent_ids: pendingSuggestionRows.map(s => s.intentId ?? 'NONE'),
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: RejectSuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildSuggestionRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
    }
    for (const [actorId, {refs}] of byActor) {
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
      })
    }
    const count = pendingSuggestionRows.length
    announce(count === 1 ? 'Suggestion rejected' : `${count} suggestions rejected`)
    focusAfterAction()
  }, [pendingSuggestionRows, environment, issueId, sendAnalyticsEvent, focusAfterAction])

  const hasFiredDisplayedRef = useRef(false)
  useEffect(() => {
    if (pendingCount > 0 && viewerCanUpdateMetadata && !isDismissed && !hasFiredDisplayedRef.current) {
      hasFiredDisplayedRef.current = true
      sendAnalyticsEvent('recent_agent_actions_panel.displayed', 'RECENT_AGENT_ACTIVITY', {
        action_count: pendingCount,
        agent_count: pendingSuggestions.groups.length,
      })
    }
  }, [pendingCount, viewerCanUpdateMetadata, isDismissed, sendAnalyticsEvent, pendingSuggestions.groups.length])

  if (pendingCount === 0 && appliedCount === 0) return null
  if (!viewerCanUpdateMetadata || isDismissed) return null

  // Require at least one identifiable agent — either from a still-pending group
  // or from a suggestion the viewer just approved (whose group has been
  // filtered out of `pendingSuggestions` once it transitioned to APPLIED).
  const hasAgent = pendingSuggestions.groups.some(g => g.agentSlug) || recentlyApproved.some(s => s.actorLogin)
  if (!hasAgent) return null

  const hasPending = pendingCount > 0

  return (
    <div
      ref={panelRef}
      className={clsx(styles.container, isFeatureEnabled('issue_inline_avatars') && styles.container_inlineAvatars)}
      data-testid="recent-agent-activity"
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2 className={styles.title}>Agent suggestions</h2>
          <Link href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" className={styles.previewLink}>
            <Label variant="secondary">Private preview</Label>
          </Link>
        </div>
        <div className={styles.headerActions}>
          {hasPending && (
            <>
              <Button variant="default" size="small" onClick={handleRejectAll}>
                Decline all
              </Button>
              <Button variant="primary" size="small" onClick={handleApproveAll}>
                Accept all
              </Button>
            </>
          )}
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label="More actions" size="small" variant="invisible" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.LinkItem href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                  Give feedback
                </ActionList.LinkItem>
                <ActionList.Item onSelect={handleDismissForIssue}>Dismiss</ActionList.Item>
                <ActionList.Item onSelect={handleDismissForever}>Never show this again</ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      </div>
      <div className={styles.chips} role="list" aria-label="Agent suggestions">
        {pendingChipItems.map(item => {
          if (item.kind === 'label-group') {
            return (
              <PendingLabelGroupChip
                key={`pending-group-${item.key}`}
                suggestions={item.suggestions}
                onApprove={handleApproveGroup}
                onReject={handleRejectGroup}
              />
            )
          }
          const {suggestion} = item
          const subjectKey =
            suggestion.issueTypeId ??
            suggestion.labelId ??
            suggestion.issueFieldId ??
            suggestion.assigneeId ??
            `${suggestion.type}:${suggestion.displayFieldName}`
          return (
            <PendingSuggestionChip
              key={`pending-${suggestion.actorId}:${subjectKey}`}
              suggestion={suggestion}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )
        })}
        {hasPending && appliedCount > 0 && <span className={styles.divider} aria-hidden="true" />}
        {recentlyApproved.map(suggestion => {
          const subjectKey =
            suggestion.issueTypeId ??
            suggestion.labelId ??
            suggestion.issueFieldId ??
            suggestion.assigneeId ??
            `${suggestion.type}:${suggestion.displayFieldName}`
          return <AppliedSuggestionChip key={`applied-${suggestion.actorId}:${subjectKey}`} suggestion={suggestion} />
        })}
      </div>
    </div>
  )
}
